import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Typography, Paper, Avatar, IconButton,
  CircularProgress, Alert, Container, Grid, Card, CardContent, CardActions, Chip, Stack,
  Skeleton
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  People as PeopleIcon,
  SearchOff
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useCompanyCore } from '../../../../core/modules/company';
import { useUserCore } from '../../../../core/modules/user';
import { useGlobalCore } from '../../../../core/modules/global';
import { User } from '../../../../core/interfaces/user.interface';
import RoleChip from '../../../components/RoleChip';
import UserFormDialog from '../../../components/user/UserFormDialog';
import UserCard from '../../../components/user/UserCard';

// --- Componente Principal de la Página ---
function ManageUsers() {
  // --- Hooks ---
  const { company } = useCompanyCore();
  const { users, getCompanyUsers, deleteUser } = useUserCore();
  const { openDialog, showSnackbar } = useGlobalCore();
  
  // --- Estado ---
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // --- Lógica de Datos ---
  const fetchData = useCallback(async () => {
    if (!company?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      // Usamos el nuevo endpoint para obtener solo los usuarios de esta compañía
      await getCompanyUsers(company.id); 
    } catch (err) {
      setError('Error al cargar los usuarios.');
    } finally {
      setIsLoading(false);
    }
  }, [company?.id, getCompanyUsers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Manejadores de Acciones ---
  const handleAddUser = () => {
    setEditingUser(null); // Poner en null indica que estamos creando
    openDialog('CREATE_OR_EDIT_USER_DIALOG'); // Un ID de diálogo genérico
  };
  
  const handleEditUser = (user: User) => {
    setEditingUser(user); // Poner el usuario indica que estamos editando
    openDialog('CREATE_OR_EDIT_USER_DIALOG');
  };

  const handleDeleteUser = (user: User) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: `Se eliminará al usuario "${user.full_name}". Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteUser(user.id, 
          () => { // onSuccess
            showSnackbar({ message: 'Usuario eliminado con éxito', options: { variant: 'success' } });
            fetchData(); // Recargar la lista
          },
          (error: any) => { // onFailure
            const message = error.response?.data?.detail || 'No se pudo eliminar el usuario.';
            showSnackbar({ message, options: { variant: 'error' } });
          }
        );
      }
    });
  };

  return (
    <>
      <Container maxWidth="xl" className="py-8 px-4 sm:px-6 lg:px-8">
        <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <Box>
            <Typography variant="h4" component="h1" className="font-bold flex items-center gap-3">
              <PeopleIcon fontSize="large"/>
              Gestionar Usuarios
            </Typography>
            <Typography color="text.secondary">
              Añade, edita o elimina miembros de tu equipo.
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={handleAddUser}
            className="w-full sm:w-auto"
          >
            Añadir Usuario
          </Button>
        </Box>

        {error && <Alert severity="error" className="mb-4">{error}</Alert>}
        
        <Grid container spacing={3}>
          {isLoading ? (
            // --- LOADER MEJORADO CON SKELETONS ---
            Array.from(new Array(8)).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Stack spacing={2} className="p-4 border rounded-lg">
                  <Stack direction="row" spacing={2} className="items-center justify-center">
                    <Skeleton variant="circular" width={80} height={80} />
                  </Stack>
                  <Skeleton variant="text" sx={{ fontSize: '1.25rem' }} />
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                  <Skeleton variant="rounded" width={100} height={24} className="mx-auto" />
                </Stack>
              </Grid>
            ))
          ) : users.length > 0 ? (
            // Contenido real
            users.map((user) => (
              <UserCard 
                key={user.id}
                user={user}
                onEdit={() => handleEditUser(user)}
                onDelete={() => handleDeleteUser(user)}
              />
            ))
          ) : (
            // Estado vacío mejorado
            <Grid item xs={12}>
              <Paper className="p-8 sm:p-16 text-center text-gray-500 flex flex-col items-center gap-4 border-dashed">
                <SearchOff sx={{ fontSize: 48 }} />
                <Typography variant="h6" className="font-semibold">No se encontraron usuarios</Typography>
                <Typography>Parece que no hay usuarios en esta empresa. ¡Añade el primero!</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>
      
      <UserFormDialog 
        userToEdit={editingUser} 
        onSuccess={fetchData}
      />
    </>
  );
}

export default ManageUsers;